/**
 * 日志管理页面
 * 
 * 功能：
 * 1. 分服务显示日志
 * 2. 跨服务请求追踪（Request ID）
 * 3. 动态切换日志级别
 */

import { useState, useEffect, useCallback, memo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, RefreshCw, Search, Loader2, FileText,
  Activity, Wifi, WifiOff,
  ChevronDown, ChevronRight,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { ScrollArea } from '@/shared/components/ui/scroll-area'
import { useToast } from '@/shared/hooks/useToast'
import {
  logApi,
  SERVICE_KEYS,
  LOG_LEVELS,
  LOG_LEVEL_COLORS,
  type ServiceInfo,
  type LogEntry,
  type TraceLogEntry,
  type ServiceKey,
} from '../services/log-api'

// ==================== Helper ====================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatChinaTime(raw: string): string {
  // 后端已返回中国时间，直接使用原始格式
  return raw
}

function LevelBadge({ level }: { level: string }) {
  const cls = LOG_LEVEL_COLORS[level] || 'bg-gray-100 text-gray-600'
  return (
    <Badge variant="outline" className={`border-0 ${cls}`}>
      {level}
    </Badge>
  )
}

/** 可折叠的日志消息：超过阈值或含换行时默认折叠，点击展开完整内容。 */
const CollapsibleMessage = memo(function CollapsibleMessage({ message }: { message: string }) {
  const [expanded, setExpanded] = useState(false)
  const isLong = message.length > 60 || message.includes('\n')
  const compactPreview = message.replace(/\s+/g, ' ').trim()

  if (!isLong) {
    return <span className="whitespace-pre-wrap break-all text-xs">{message}</span>
  }

  return (
    <div className="text-xs space-y-1">
      <button
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
        )}
        <span>{expanded ? '收起' : '展开'}</span>
      </button>
      {expanded ? (
        <pre className="whitespace-pre-wrap break-all m-0 font-mono text-xs leading-relaxed">{message}</pre>
      ) : (
        <div className="truncate">{compactPreview}</div>
      )}
    </div>
  )
})

const LOG_PAGE_SIZE = 100

// ==================== Main Component ====================

export default function LogManagement() {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToast()

  // --- Services ---
  const [services, setServices] = useState<ServiceInfo[]>([])
  const [isLoadingServices, setIsLoadingServices] = useState(true)

  // --- Log Query ---
  const [selectedService, setSelectedService] = useState<ServiceKey>('axiom-agent-admin')
  const [levelFilter, setLevelFilter] = useState<string>('all')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [requestIdFilter, setRequestIdFilter] = useState('')
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [logTotal, setLogTotal] = useState(0)
  const [logHasMore, setLogHasMore] = useState(false)
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)
  const [logOffset, setLogOffset] = useState(0)
  // --- Diagnostic metadata from API ---
  const [logSource, setLogSource] = useState('')
  const [logRawLines, setLogRawLines] = useState<number | undefined>()
  const [logMergedEntries, setLogMergedEntries] = useState<number | undefined>()
  const [logParsedEntries, setLogParsedEntries] = useState<number | undefined>()
  const [hideNoiseLogs, setHideNoiseLogs] = useState(true)

  // --- Level Change ---
  const [isChangingLevel, setIsChangingLevel] = useState(false)

  // --- Tabs ---
  const [activeTab, setActiveTab] = useState('logs')

  // --- Trace ---
  const [traceRequestId, setTraceRequestId] = useState('')
  const [traceLogs, setTraceLogs] = useState<TraceLogEntry[]>([])
  const [traceServices, setTraceServices] = useState<string[]>([])
  const [isTracing, setIsTracing] = useState(false)
  const [traceTotal, setTraceTotal] = useState(0)
  const [traceErrors, setTraceErrors] = useState<Array<{ service: string; error: string }>>([])

  // ==================== Load Services ====================

  const loadServices = useCallback(async () => {
    setIsLoadingServices(true)
    try {
      const data = await logApi.getServices()
      setServices(data)
    } catch (err) {
      console.error('Failed to load services:', err)
      showError('获取服务列表失败')
    } finally {
      setIsLoadingServices(false)
    }
  }, [showError])

  useEffect(() => {
    loadServices()
  }, [loadServices])

  // ==================== Query Logs ====================

  const queryLogs = useCallback(async (offset = 0, forceRequestId?: string) => {
    setIsLoadingLogs(true)
    try {
      const effectiveRequestId = forceRequestId !== undefined ? forceRequestId : requestIdFilter.trim()
      const result = await logApi.queryLogs({
        service: selectedService,
        level: levelFilter !== 'all' ? levelFilter : undefined,
        search: searchKeyword || undefined,
        request_id: effectiveRequestId || undefined,
        lines: LOG_PAGE_SIZE,
        offset,
      })
      if (offset === 0) {
        setLogs(result.logs)
      } else {
        setLogs(prev => [...prev, ...result.logs])
      }
      setLogTotal(result.total)
      setLogHasMore(result.has_more)
      setLogOffset(offset)
      setLogSource(result.source || '')
      setLogRawLines(result.raw_lines)
      setLogMergedEntries(result.merged_entries)
      setLogParsedEntries(result.parsed_entries)
    } catch (err) {
      console.error('Failed to query logs:', err)
      showError('查询日志失败')
    } finally {
      setIsLoadingLogs(false)
    }
  }, [selectedService, levelFilter, searchKeyword, requestIdFilter, showError])

  // Auto-query when service/filter changes
  useEffect(() => {
    queryLogs(0)
  }, [selectedService, levelFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    queryLogs(0)
  }

  const handleLoadMore = () => {
    queryLogs(logOffset + LOG_PAGE_SIZE)
  }

  // ==================== Change Level ====================

  const handleChangeLevel = async (service: string, newLevel: string) => {
    setIsChangingLevel(true)
    try {
      const result = await logApi.setLogLevel(service, newLevel)
      showSuccess(`${result.service}: ${result.old_level} -> ${result.new_level}`)
      // Refresh service list
      await loadServices()
    } catch (err) {
      console.error('Failed to change log level:', err)
      showError('切换日志级别失败')
    } finally {
      setIsChangingLevel(false)
    }
  }

  // ==================== Trace ====================

  const handleTrace = async () => {
    if (!traceRequestId.trim()) return
    setIsTracing(true)
    setTraceLogs([])
    setTraceServices([])
    setTraceErrors([])
    try {
      const result = await logApi.traceRequest(traceRequestId.trim())
      setTraceLogs(result.logs)
      setTraceServices(result.services)
      setTraceTotal(result.total)
      setTraceErrors(result.errors || [])
      if (result.total === 0) {
        showError('未找到匹配的日志条目')
      }
    } catch (err) {
      console.error('Failed to trace request:', err)
      showError('请求追踪失败')
    } finally {
      setIsTracing(false)
    }
  }

  const applyRequestIdFilter = (id: string) => {
    setRequestIdFilter(id)
    setSearchKeyword('')
    setActiveTab('logs')
    queryLogs(0, id)
  }

  // ==================== Render: Service Cards ====================

  const currentServiceInfo = services.find(
    s => s.service?.toLowerCase() === selectedService || s.service?.toLowerCase().includes(selectedService)
  )

  const visibleLogs = logs.filter((log) => {
    if (!hideNoiseLogs) return true
    const module = (log.module || '').toLowerCase()
    return !(
      module === 'logging' ||
      module.includes('middleware.request_logging')
    )
  })

  // ==================== Render ====================

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <span className="font-bold text-xl tracking-tight">日志管理</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/runtime/monitoring')} className="gap-2">
              <Activity className="h-4 w-4" />
              运行时
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { loadServices(); queryLogs(0) }}
              className="gap-2"
              disabled={isLoadingServices}
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingServices ? 'animate-spin' : ''}`} />
              刷新
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-4 py-8">
        {/* Service Status Cards */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3">服务状态</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {SERVICE_KEYS.map(({ key, label }) => {
              const info = services.find(
                s => s.service?.toLowerCase() === key || s.service?.toLowerCase().includes(key)
              )
              const reachable = info?.reachable ?? false
              return (
                <Card
                  key={key}
                  className={`cursor-pointer transition-all ${
                    selectedService === key
                      ? 'ring-2 ring-primary border-primary'
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => setSelectedService(key)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium truncate">{label}</span>
                      {reachable ? (
                        <Wifi className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                      ) : (
                        <WifiOff className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />
                      )}
                    </div>
                    {info && reachable ? (
                      <div className="space-y-1">
                        <LevelBadge level={info.current_level} />
                        {info.file_exists && (
                          <p className="text-[10px] text-muted-foreground">
                            {formatFileSize(info.file_size)}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {isLoadingServices ? '...' : '不可达'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="logs">服务日志</TabsTrigger>
            <TabsTrigger value="trace">请求追踪</TabsTrigger>
          </TabsList>

          {/* ==================== Tab: Service Logs ==================== */}
          <TabsContent value="logs" className="space-y-4">
            {/* Toolbar */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-end gap-3">
                  {/* Service Select */}
                  <div className="space-y-1">
                    <Label className="text-xs">服务</Label>
                    <Select value={selectedService} onValueChange={(v) => setSelectedService(v as ServiceKey)}>
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_KEYS.map(({ key, label }) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Level Filter */}
                  <div className="space-y-1">
                    <Label className="text-xs">级别</Label>
                    <Select value={levelFilter} onValueChange={setLevelFilter}>
                      <SelectTrigger className="w-[130px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">全部级别</SelectItem>
                        {LOG_LEVELS.map(level => (
                          <SelectItem key={level} value={level}>{level}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Search */}
                  <div className="space-y-1 flex-1 min-w-[200px]">
                    <Label className="text-xs">搜索</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="搜索关键词..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                      />
                      <Button variant="outline" onClick={handleSearch} disabled={isLoadingLogs}>
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Request ID Filter */}
                  <div className="space-y-1 min-w-[220px]">
                    <Label className="text-xs">Request ID</Label>
                    <Input
                      placeholder="req_xxx 或 uuid..."
                      value={requestIdFilter}
                      onChange={(e) => setRequestIdFilter(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-1 shrink-0">
                    <Label className="text-xs">操作</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 px-2 text-xs whitespace-nowrap"
                        onClick={() => {
                          setRequestIdFilter('')
                          setSearchKeyword('')
                          queryLogs(0, '')
                        }}
                      >
                        清空筛选
                      </Button>
                    </div>
                  </div>

                  {/* Noise Filter */}
                  <div className="space-y-1 shrink-0">
                    <Label className="text-xs">显示</Label>
                    <Button
                      variant={hideNoiseLogs ? 'default' : 'outline'}
                      size="sm"
                      className="h-9 px-2 text-xs whitespace-nowrap"
                      onClick={() => setHideNoiseLogs((v) => !v)}
                    >
                      {hideNoiseLogs ? '已隐藏噪声日志' : '显示噪声日志'}
                    </Button>
                  </div>

                  {/* Level Change */}
                  {currentServiceInfo?.reachable && (
                    <div className="space-y-1">
                      <Label className="text-xs">切换级别</Label>
                      <Select
                        value={currentServiceInfo.current_level}
                        onValueChange={(v) => handleChangeLevel(selectedService, v)}
                        disabled={isChangingLevel}
                      >
                        <SelectTrigger className="w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LOG_LEVELS.map(level => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Log Table */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">
                    {SERVICE_KEYS.find(s => s.key === selectedService)?.label} 日志
                  </CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {isLoadingLogs ? '加载中...' : `${visibleLogs.length} / ${logTotal} 条`}
                  </span>
                </div>
                {/* Diagnostic info bar */}
                {!isLoadingLogs && logSource && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground font-mono mt-1">
                    <span title="日志文件路径">
                      <FileText className="inline h-3 w-3 mr-1" />
                      {logSource}
                    </span>
                    {logRawLines !== undefined && (
                      <span title="文件原始行数">原始行: {logRawLines.toLocaleString()}</span>
                    )}
                    {logMergedEntries !== undefined && (
                      <span title="多行合并后的条目数">合并后: {logMergedEntries.toLocaleString()}</span>
                    )}
                    {logParsedEntries !== undefined && (
                      <span title="成功解析的条目数">已解析: {logParsedEntries.toLocaleString()}</span>
                    )}
                    {logRawLines !== undefined && logMergedEntries !== undefined && logRawLines > logMergedEntries && (
                      <span className="text-yellow-600 dark:text-yellow-400" title="有多行日志被合并（如 System Prompt 等多行内容）">
                        含多行合并
                      </span>
                    )}
                    {hideNoiseLogs && (
                      <span className="text-blue-600 dark:text-blue-400" title="前端隐藏了 logging 与 request_logging 噪声日志">
                        已隐藏噪声日志
                      </span>
                    )}
                  </div>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">时间（中国）</TableHead>
                        <TableHead className="w-[70px]">级别</TableHead>
                        <TableHead className="w-[240px]">来源</TableHead>
                        <TableHead>消息</TableHead>
                        <TableHead className="w-[140px]">Request ID</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoadingLogs && visibleLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                            <p className="text-muted-foreground">加载中...</p>
                          </TableCell>
                        </TableRow>
                      ) : visibleLogs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-12">
                            <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                            <p className="text-muted-foreground">暂无可见日志</p>
                            <p className="text-xs text-muted-foreground/60 mt-1">
                              {hideNoiseLogs ? '当前筛选隐藏了 logging/middleware，点上方按钮可查看全部' : '该服务可能没有配置日志文件，或日志文件为空'}
                            </p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        visibleLogs.map((log, idx) => (
                          <TableRow key={idx} className="font-mono text-xs align-top">
                            <TableCell className="text-muted-foreground whitespace-nowrap">
                              {formatChinaTime(log.timestamp)}
                            </TableCell>
                            <TableCell>
                              <LevelBadge level={log.level} />
                            </TableCell>
                            <TableCell className="text-muted-foreground max-w-[200px]" title={`${log.module}:${log.function}:${log.line}`}>
                              <div className="truncate">{log.module}</div>
                              <div className="text-[10px] text-muted-foreground/70 truncate">
                                {log.function}:{log.line}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[500px]">
                              <CollapsibleMessage message={log.message} />
                            </TableCell>
                            <TableCell>
                              {log.request_id ? (
                                <button
                                  className="text-blue-600 hover:underline text-xs font-mono"
                                  onClick={() => {
                                    setTraceRequestId(log.request_id!)
                                    setActiveTab('trace')
                                  }}
                                  title="点击追踪此请求"
                                >
                                  {log.request_id}
                                </button>
                              ) : (
                                <span className="text-muted-foreground/40">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>

                {/* Load More */}
                {logHasMore && (
                  <div className="p-3 border-t text-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLoadMore}
                      disabled={isLoadingLogs}
                    >
                      {isLoadingLogs ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      加载更多（已加载 {logs.length} / {logTotal}）
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==================== Tab: Request Trace ==================== */}
          <TabsContent value="trace" className="space-y-4">
            {/* Search Bar */}
            <Card>
              <CardContent className="p-4">
                <Label className="text-sm font-medium mb-2 block">Request ID</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="输入 Request ID（如 req_abc123def456）"
                    value={traceRequestId}
                    onChange={(e) => setTraceRequestId(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleTrace() }}
                    className="font-mono"
                  />
                  <Button onClick={handleTrace} disabled={isTracing || !traceRequestId.trim()} className="gap-2">
                    {isTracing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                    追踪
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Trace Result */}
            {(traceLogs.length > 0 || isTracing) && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">追踪结果</CardTitle>
                    {traceTotal > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{traceTotal} 条日志</span>
                        <span className="text-sm text-muted-foreground">|</span>
                        <span className="text-sm text-muted-foreground">
                          涉及 {traceServices.length} 个服务
                        </span>
                        {traceErrors.length > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">|</span>
                            <span className="text-sm text-amber-600">
                              {traceErrors.length} 个服务查询失败
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Service chips */}
                  {traceServices.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-muted-foreground">涉及服务:</span>
                      {traceServices.map((svc, i) => (
                        <Badge key={svc} variant="outline" className="text-xs">
                          {SERVICE_KEYS.find(s => s.key === svc)?.label || svc}
                          {i < traceServices.length - 1 && (
                            <span className="ml-1 text-muted-foreground">&rarr;</span>
                          )}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {traceErrors.length > 0 && (
                    <div className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                      部分服务查询失败：{traceErrors.map((e) => e.service).join(', ')}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <Table className="table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[200px]">时间（中国）</TableHead>
                          <TableHead className="w-[140px]">服务</TableHead>
                          <TableHead className="w-[80px]">级别</TableHead>
                          <TableHead className="w-[180px]">Request ID</TableHead>
                          <TableHead>消息</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isTracing ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-12">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-primary" />
                              <p className="text-muted-foreground">正在扫描所有服务...</p>
                            </TableCell>
                          </TableRow>
                        ) : traceLogs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-12">
                              <Search className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                              <p className="text-muted-foreground">输入 Request ID 开始追踪</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          traceLogs.map((log, idx) => (
                            <TableRow key={idx} className="font-mono text-xs">
                              <TableCell className="text-muted-foreground whitespace-nowrap">
                                {formatChinaTime(log.timestamp)}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-xs">
                                  {SERVICE_KEYS.find(s => s.key === log.service)?.label || log.service}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <LevelBadge level={log.level} />
                              </TableCell>
                              <TableCell>
                                {log.request_id ? (
                                  <button
                                    className="text-blue-600 hover:underline text-xs font-mono"
                                    onClick={() => applyRequestIdFilter(log.request_id!)}
                                    title="回填到日志筛选"
                                  >
                                    {log.request_id}
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground/40">-</span>
                                )}
                              </TableCell>
                              <TableCell className="max-w-[500px]">
                                <CollapsibleMessage message={log.message} />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}
